import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Drawer } from 'antd';

import DrawerSubmitFooter from '../../common/containers/DrawerSubmitFooter';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import ConfirmPasswordModal from '../../settings/ConfirmPasswordModal';
import { resetProjectScheduleOfValues } from '../state/projects.actions';
import { SOV_RESET_COLUMN_LABELS } from './helpers';

export default function ProjectScheduleOfValuesResetDrawer({
  visible,
  onClose,
  projectId,
}) {
  const dispatch = useDispatch();
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const onColumnChange = useCallback((columnValue) => (checked) => {
    setSelectedColumns((prev) => {
      if (checked) {
        return [...prev, columnValue];
      }
      return prev.filter((value) => value !== columnValue);
    });
  }, []);

  const handleSubmit = useCallback(() => {
    if (!selectedColumns.length) return;
    setIsPasswordModalVisible(true);
  }, [selectedColumns]);

  const handlePasswordConfirm = useCallback(async (password) => {
    setIsLoading(true);
    const result = await dispatch(resetProjectScheduleOfValues({
      projectId,
      columns: selectedColumns,
      password,
    }));
    setIsLoading(false);
    if (result) {
      setSelectedColumns([]);
      onClose();
    }
    return result;
  }, [projectId, selectedColumns, onClose]);

  const handleClose = useCallback(() => {
    setSelectedColumns([]);
    onClose();
  }, [onClose]);

  return (
    <>
      <Drawer
        title="Reset Schedule of Values"
        visible={visible}
        onClose={handleClose}
        width={500}
        maskClosable={false}
        footer={(
          <DrawerSubmitFooter
            onClose={handleClose}
            onSubmit={handleSubmit}
            canSubmit={selectedColumns.length > 0}
            submitTitle="Reset"
            loading={isLoading}
          />
        )}
      >
        <p style={{ marginBottom: 16 }}>
          Select the columns to zero out for all rows:
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {Object.entries(SOV_RESET_COLUMN_LABELS).map(([value, label]) => (
            <OnTraccrCheckbox
              key={value}
              label={label}
              value={selectedColumns.includes(value)}
              onChange={onColumnChange(value)}
            />
          ))}
        </div>
      </Drawer>
      <ConfirmPasswordModal
        visible={isPasswordModalVisible}
        onClose={() => setIsPasswordModalVisible(false)}
        onConfirm={handlePasswordConfirm}
      />
    </>
  );
}

ProjectScheduleOfValuesResetDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.string,
};

ProjectScheduleOfValuesResetDrawer.defaultProps = {
  projectId: null,
};
